import { LoadingStatusValues, MAX_RELATED_CONTENT } from '@/utils/constants';
import { getPageUrl } from '@/utils/helpers';
import { resolveDate } from '@/utils/resolvers';
import React from 'react';
import ElasticAdapter from '../../Adapters/ElasticAdapter';
import { ElasticComponentContextType } from '../../ElasticComponent/ElasticComponentContext';
import { useElasticSearch } from '../useElasticSearch';
import { DEFAULT_ELASTIC_FIELDS } from './useElasticSearch.constants';
import { parseKeysToFilters, parseSortByPublicationDate } from './useElasticSearch.utils';

const useElasticRelated = ({
  keys = [],
  pageModels,
  labels = {},
  isElasticAllowed,
  relatedContentList = [],
  contentCategoryFallback = [],
  sortBy = '',
  facetsList = [],
  buttonLink = {},
  page,
  excludedFilters = [],
  size = '3',
}: any) => {
  const facetsListWithContentCategory = React.useMemo(
    () => ['tax_content_category', ...facetsList],
    [facetsList],
  );
  const filters = parseKeysToFilters(facetsList, pageModels?.taxonomy, keys);
  const defaultParams = React.useRef({
    q: '',
    page: '1',
    sortBy,
    size,
    content_category: [],
    keywords: '',
    filters,
  });

  // Search Params
  const search = React.useMemo(
    () => ({
      q: defaultParams.current.q,
      page: defaultParams.current.page,
      sortBy: defaultParams.current.sortBy,
      keywords: defaultParams.current.keywords,
      size: defaultParams.current.size,
      filters: ElasticAdapter.encodeFilters(
        {
          ...defaultParams.current.filters,
          content_category: [],
        },
        facetsListWithContentCategory.filter(
          (item, pos) => facetsListWithContentCategory.indexOf(item) === pos,
        ),
      ),
    }),
    [facetsListWithContentCategory],
  );

  const sortOptions: string[] = [];

  const [status, setStatus] = React.useState<ElasticComponentContextType['status']>(
    LoadingStatusValues.initial,
  );

  const { esLabels } = pageModels;
  const { elasticsearch: elasticsearchConfig } = pageModels.config || {};
  const { search_key: searchKey, resources_engine: engine } = elasticsearchConfig || {};
  const allLabels = React.useMemo(
    () => ({
      ...labels,
    }),
    [labels],
  );

  const es = useElasticSearch({
    engine,
    searchParams: search,
    facets: facetsList,
    fields: DEFAULT_ELASTIC_FIELDS,
    useDynamicFacets: false,
    hideFilters: ['tax_content_category'],
    initBodyFilters: ['tax_content_category'],
    searchKey: isElasticAllowed ? searchKey : '',
    parseSortBy: parseSortByPublicationDate,
    filtersFallback: { tax_content_category: contentCategoryFallback },
    language: pageModels.language,
    labels: esLabels,
    excludedFilters,
  });

  React.useEffect(
    () => {
      if (!isElasticAllowed) return;
      if (!es.isLoading && status === LoadingStatusValues.initial) return;

      setStatus(() => (es.isLoading ? LoadingStatusValues.loading : LoadingStatusValues.ready));
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [es.isLoading],
  );

  const buttonLinkDefault = React.useMemo(
    () => ({
      buttonStyle: 'secondary',
      displayText: allLabels?.read_more || 'Read More',
    }),
    [allLabels?.read_more],
  );

  const curatedRelatedContent = React.useMemo(() => {
    if (!isElasticAllowed) return relatedContentList;
    if (es?.isLoading) return [];

    const MAX_REMAINING_RELATED_CONTENT = MAX_RELATED_CONTENT - relatedContentList?.length;

    const REMAINING_RELATED_CONTENT = es?.response?.results
      ?.map((resultItem: any) => {
        return {
          cardHeading: resultItem?.title?.raw,
          cardDescription: resultItem?.description?.raw,
          buttonLink: {
            ...buttonLinkDefault,
            ...buttonLink,
            href: getPageUrl({ url: resultItem?.url?.raw || '', page }),
          },
          ...(!!resultItem?.image?.raw &&
            page && {
              cardImage: {
                altText: resultItem?.title?.raw,
                original: {
                  url: getPageUrl({ url: resultItem?.image?.raw || '', page }),
                },
                small: {
                  url: getPageUrl({ url: resultItem?.image?.raw || '', page }),
                },
              },
            }),
          cardPublicationDate: resolveDate({ rawVal: resultItem?.publication_date?.raw }),
          cardCategory: resultItem?.tax_content_category?.raw?.[0] || '',
        };
      })
      ?.slice(0, MAX_REMAINING_RELATED_CONTENT);

    return [...relatedContentList, ...REMAINING_RELATED_CONTENT];
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [allLabels?.read_more, es?.isLoading, isElasticAllowed, relatedContentList]);

  return {
    es,
    curatedRelatedContent,
    search,
    sortOptions,
    status,
    allLabels,
    engine,
    searchKey,
  };
};

export default useElasticRelated;
